import React, { forwardRef } from 'react'
import { cn } from '../../utils/cn'
import { useTheme } from '../providers/ThemeProvider'

// ═══════════════════════════════════════════════════════════════════════════
// Input - Handwritten Line Style for Diary Theme
// ═══════════════════════════════════════════════════════════════════════════

export interface InputProps extends React.InputHTMLAttributes<HTMLInputElement> {
  /** Input variant for diary theme */
  variant?: 'default' | 'underline' | 'filled'
}

const Input = forwardRef<HTMLInputElement, InputProps>(
  ({ className, variant = 'default', type = 'text', style, ...props }, ref) => {
    const { isDiary, diaryFontFamily } = useTheme()
    
    // Diary theme styles - Handwritten line aesthetic
    const getDiaryStyles = (): React.CSSProperties => {
      const baseStyles: React.CSSProperties = {
        fontFamily: 'var(--font-body)',
        color: 'var(--ink-black)',
        transition: 'all 0.2s ease',
      }
      
      switch (variant) {
        case 'underline':
          // Classic journal line
          return {
            ...baseStyles,
            fontFamily: diaryFontFamily,
            backgroundColor: 'transparent',
            border: 'none',
            borderBottom: '1px dashed var(--ink-faded)',
            borderRadius: '0',
            paddingLeft: '0',
            paddingRight: '0',
          }
        case 'filled':
          // Paper fill
          return {
            ...baseStyles,
            backgroundColor: 'var(--paper-aged)',
            border: '1px solid var(--paper-margin)',
            borderRadius: '4px',
          }
        default:
          // Standard with paper aesthetics
          return {
            ...baseStyles,
            backgroundColor: 'var(--paper-cream)',
            border: '1px solid var(--paper-margin)',
            borderLeft: '2px solid var(--ink-faded)',
            borderRadius: '4px',
          }
      }
    }
    
    return (
      <input
        type={type}
        className={cn(
          'flex min-h-touch w-full rounded-md px-3 py-2 text-sm file:border-0 file:bg-transparent file:text-sm file:font-medium',
          !isDiary && [
            'border border-soft hover:border-strong',
            'bg-surface',
            'text-primary',
            'placeholder:text-muted',
            // 2026-08-29 UI audit: the ring was --brand-subtle-border, a hairline
            // tint that reads as no ring at all next to the border it sits on.
            // Same brand ring + surface offset as Button and Textarea now.
            'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand focus-visible:ring-offset-2 focus-visible:ring-offset-surface focus-visible:border-brand',
          ],
          isDiary && [
            'placeholder:text-[var(--ink-faded)]',
            'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--horton-purple)] focus-visible:ring-offset-1 focus-visible:ring-offset-surface',
            // `border-b-solid` emits zero css (no such utility) — 2026-08-29
            variant === 'underline' && 'focus:border-b-[var(--ink-sepia)] focus:[border-bottom-style:solid]',
          ],
          'disabled:cursor-not-allowed disabled:opacity-50',
          className
        )}
        style={isDiary ? { ...getDiaryStyles(), ...style } : style}
        ref={ref}
        {...props}
      />
    )
  }
)
Input.displayName = 'Input'

export { Input }
